import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Autocomplete,
  Stack, IconButton, Box, Typography, Select, MenuItem, FormControl, InputLabel, CircularProgress, Alert
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useTranslation } from 'react-i18next';
import supabase from '../utils/supabase';

// jsonbオブジェクトをUI用の配列に変換
const jsonbToArray = (jsonb) => {
  if (!jsonb) return [];
  return Object.entries(jsonb).map(([lang, value]) => ({ lang, value }));
};

// UI用の配列をjsonbオブジェクトに変換
const arrayToJsonb = (array) => {
  return array.reduce((acc, { lang, value }) => {
    if (lang && value) {
      acc[lang] = value;
    }
    return acc;
  }, {});
};

// ResearcherSelector.jsxのロジックを参考に、研究者の氏名を組み立てるヘルパー関数
const getResearcherFullName = (researcher) => {
  if (!researcher) return '';
  const parts = [
    researcher.last_name || "",
    researcher.first_name ? `, ${researcher.first_name}` : "",
    researcher.middle_name ? ` ${researcher.middle_name}` : "",
    researcher.name_particle ? ` ${researcher.name_particle}` : "",
  ]
    .join("")
    .trim();
  return parts || researcher.id || '';
};


const MultiLanguageField = ({ label, entries, onEntriesChange }) => {
  const handleEntryChange = (index, field, value) => {
    const newEntries = [...entries];
    newEntries[index][field] = value;
    onEntriesChange(newEntries);
  };

  const addEntry = () => {
    onEntriesChange([...entries, { lang: 'en', value: '' }]);
  };

  const removeEntry = (index) => {
    const newEntries = entries.filter((_, i) => i !== index);
    onEntriesChange(newEntries);
  };

  return (
    <Box sx={{ mb: 3, p: 2, border: '1px solid #444', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>{label}</Typography>
      <Stack spacing={2}>
        {entries.map((entry, index) => (
          <Stack direction="row" spacing={1} key={index} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel>Lang</InputLabel>
              <Select
                label="Lang"
                value={entry.lang}
                onChange={(e) => handleEntryChange(index, 'lang', e.target.value)}
              >
                <MenuItem value="en">EN</MenuItem>
                <MenuItem value="ja">JA</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Value"
              value={entry.value}
              onChange={(e) => handleEntryChange(index, 'value', e.target.value)}
              multiline
            />
            <IconButton onClick={() => removeEntry(index)} size="small">
              <RemoveCircleOutlineIcon />
            </IconButton>
          </Stack>
        ))}
        <Button startIcon={<AddCircleOutlineIcon />} onClick={addEntry} size="small">
          Add language
        </Button>
      </Stack>
    </Box>
  );
};

export default function UserInfoEditDialog({ open, onClose, user }) {
  const { t, i18n } = useTranslation('common');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [memberData, setMemberData] = useState(null);

  // フォームの状態
  const [avatarUrl, setAvatarUrl] = useState('');
  const [displayNameEntries, setDisplayNameEntries] = useState([]);
  const [roleEntries, setRoleEntries] = useState([]);
  const [affiliationEntries, setAffiliationEntries] = useState([]);
  const [descriptionEntries, setDescriptionEntries] = useState([]);
  const [researchers, setResearchers] = useState([]);
  const [selectedResearcher, setSelectedResearcher] = useState(null);
  const [researcherInputValue, setResearcherInputValue] = useState('');
  const [researcherSearchLoading, setResearcherSearchLoading] = useState(false);
  const [authorshipOrder, setAuthorshipOrder] = useState(0);

  // userオブジェクトやi18nオブジェクトが再レンダリングのたびに新しいインスタンスとして
  // 生成される可能性があるため、エフェクトの依存配列には安定したプリミティブ値を渡します。
  // これにより、入力中の意図しないデータ再取得を防ぎます。
  const userEmail = user?.email;
  const userDisplayName = user?.user_metadata?.display_name;
  const currentLang = i18n.language;

  // ダイアログが閉じたときに一時的な状態（成功・エラーメッセージ）をリセットします
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setSuccess(false);
        setError(null);
      }, 300); // 閉じるアニメーションの後にリセット
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userEmail) return;
      setLoading(true);
      setError(null);
      setResearchers([]); // 以前の検索結果をクリア
      try {
        const { data: memberData, error: memberError } = await supabase
          .from('admin_team_members')
          .select('*')
          .eq('email', userEmail)
          .single();

        if (memberError && memberError.code !== 'PGRST116') throw memberError;

        // メンバー情報に基づいてフォームの状態を設定
        if (memberData) {
          setMemberData(memberData);
          setAvatarUrl(memberData.avatar_url || '');
          setDisplayNameEntries(jsonbToArray(memberData.display_name));
          setRoleEntries(jsonbToArray(memberData.role));
          setAffiliationEntries(jsonbToArray(memberData.affiliation));
          setDescriptionEntries(jsonbToArray(memberData.description));
          setAuthorshipOrder(memberData.authorship_order ?? 0);
          
          // 紐付けられた研究者がいる場合、その詳細情報を取得
          if (memberData.linked_researcher_id) {
            const { data: researcherData, error: researcherError } = await supabase
              .from('researchers')
              .select('id, last_name, first_name, middle_name, name_particle')
              .eq('id', memberData.linked_researcher_id)
              .single();
            
            if (researcherError) throw researcherError;

            if (researcherData) {
              setSelectedResearcher(researcherData);
              // Autocompleteのオプションに初期値を設定
              setResearchers([researcherData]); 
            } else {
              setSelectedResearcher(null);
            }
          } else {
            setSelectedResearcher(null);
          }
        } else {
          // 新規プロフィールのための初期値設定
          setMemberData(null);
          setAvatarUrl('');
          setDisplayNameEntries([{ lang: currentLang, value: userDisplayName || '' }]);
          setRoleEntries([{ lang: currentLang, value: '' }]);
          setAffiliationEntries([{ lang: currentLang, value: '' }]);
          setDescriptionEntries([{ lang: currentLang, value: '' }]);
          setAuthorshipOrder(0);
          setSelectedResearcher(null);
        }
      } catch (err) {
        console.error("Failed to fetch dialog data:", err);
        setError("Failed to load profile data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    if (open) {
      fetchData();
    }
  }, [open, userEmail, userDisplayName, currentLang]);

  // 研究者の動的検索用のuseEffect
  useEffect(() => {
    const searchResearchers = async () => {
      if (!researcherInputValue.trim()) {
        setResearcherSearchLoading(false);
        setResearchers(selectedResearcher ? [selectedResearcher] : []);
        return;
      }
      setResearcherSearchLoading(true);
      const searchTerm = `%${researcherInputValue}%`;
      const { data, error } = await supabase
        .from('researchers')
        .select('id, last_name, first_name, middle_name, name_particle')
        .or(`last_name.ilike.${searchTerm},first_name.ilike.${searchTerm}`)
        .limit(20);
      setResearcherSearchLoading(false);
      if (error) return console.error('Error fetching researchers:', error);
      let newOptions = data || [];
      if (selectedResearcher && !newOptions.some(o => o.id === selectedResearcher.id)) newOptions.unshift(selectedResearcher);
      setResearchers(newOptions);
    };
    const timer = setTimeout(() => { searchResearchers(); }, 500); // 500ms debounce
    return () => clearTimeout(timer);
  }, [researcherInputValue, selectedResearcher]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    const displayNameJson = arrayToJsonb(displayNameEntries);
    const payload = {
      email: userEmail, // onConflictのためにemailをペイロードに含めます
      avatar_url: avatarUrl,
      display_name: displayNameJson,
      role: arrayToJsonb(roleEntries),
      affiliation: arrayToJsonb(affiliationEntries),
      description: arrayToJsonb(descriptionEntries),
      linked_researcher_id: selectedResearcher ? selectedResearcher.id : null,
      authorship_order: authorshipOrder,
    };

    try {
      // upsertを使用して、emailに基づいてレコードを挿入または更新します。
      // これにより、レコードが既に存在する場合の重複キーエラーを防ぎます。
      const { error: upsertError } = await supabase
        .from('admin_team_members')
        .upsert(payload, { onConflict: 'email' });

      if (upsertError) throw upsertError;

      const currentLang = i18n.language;
      const newDisplayNameForAuth = displayNameJson[currentLang] || displayNameJson.en || '';
      if (newDisplayNameForAuth && newDisplayNameForAuth !== user.user_metadata?.display_name) {
        await supabase.auth.updateUser({ data: { display_name: newDisplayNameForAuth } });
      }
      
      setSuccess(true);
      setSaving(false);
      // 成功メッセージを2秒間表示した後、ダイアログを自動で閉じます
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error("Failed to save user profile:", err);
      setError("プロフィールの保存に失敗しました。入力内容を確認して、もう一度お試しください。");
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{t('editProfile', 'Edit Your Profile')}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success">プロフィールが正常に保存されました。</Alert>}
            <TextField
              label="Avatar URL"
              fullWidth
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              helperText="e.g., /avatars/your-name.png"
            />
            <TextField
              label="Display Order"
              type="number"
              value={authorshipOrder}
              onChange={(e) => setAuthorshipOrder(parseInt(e.target.value, 10) || 0)}
              helperText="数字が小さいほどリストの上部に表示されます。"
            />
            <Autocomplete
              filterOptions={(x) => x} // サーバーサイドでフィルタリングするため、クライアントサイドのフィルタリングを無効化
              options={researchers}
              getOptionLabel={(option) => getResearcherFullName(option)}
              value={selectedResearcher}
              onChange={(event, newValue) => {
                setSelectedResearcher(newValue);
              }}
              inputValue={researcherInputValue}
              onInputChange={(event, newInputValue) => {
                setResearcherInputValue(newInputValue);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={researcherSearchLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Link to Researcher"
                  helperText="研究者名を入力して、プロフィールとデータベースの研究者情報を紐付けます。"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (<>{researcherSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>),
                  }}
                />
              )}
            />
            <MultiLanguageField label="Display Name" entries={displayNameEntries} onEntriesChange={setDisplayNameEntries} />
            <MultiLanguageField label="Role" entries={roleEntries} onEntriesChange={setRoleEntries} />
            <MultiLanguageField label="Affiliation" entries={affiliationEntries} onEntriesChange={setAffiliationEntries} />
            <MultiLanguageField label="Description" entries={descriptionEntries} onEntriesChange={setDescriptionEntries} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving || success}>{t('cancel', 'Cancel')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || saving || success}>
          {saving ? <CircularProgress size={24} /> : t('save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}